import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { navbarensUnderkant } from '../utils/navbarOffset';

/**
 * Håller reda på vilka dockade sidopaneler som är öppna — prislistan och
 * arbetsbeskrivningen i dag.
 *
 * Tre saker behöver veta det:
 *
 * 1. Modalerna. Portalens modaler är genomgående `fixed inset-0` och skulle
 *    breda ut sig under panelerna. Regeln i index.css krymper deras högerkant
 *    med `--dockade-paneler-bredd`, så att modal och panel står bredvid
 *    varandra i stället för ovanpå. Det är hela poängen med panelerna: att
 *    kunna redigera i en PDF med arbetsbeskrivningen uppslagen bredvid.
 *
 * 2. Panelerna själva. Är två öppna samtidigt måste den andra hamna till
 *    vänster om den första i stället för ovanpå den. Ordningen är den de
 *    öppnades i, så en panel som redan står still inte hoppar när nästa öppnas.
 *
 * 3. Navbaren. Panelerna ska börja under den, inte över den. Navbaren är inte
 *    sticky utan rullar bort, så avståndet mäts mot dess underkant och klampas
 *    till noll — panelen glider upp till toppen i takt med att navbaren rullar
 *    ut, i stället för att lämna en död remsa kvar.
 */
const DockadePanelerContext = createContext(null);

// Mätningen delas med firandebannern — se utils/navbarOffset.js

export const DockadePanelerProvider = ({ children }) => {
  const [paneler, setPaneler] = useState([]);
  const [toppOffset, setToppOffset] = useState(0);

  const registrera = useCallback((id, bredd) => {
    setPaneler(prev => (prev.some(p => p.id === id) ? prev : [...prev, { id, bredd }]));
  }, []);

  const avregistrera = useCallback((id) => {
    setPaneler(prev => prev.filter(p => p.id !== id));
  }, []);

  const totalBredd = paneler.reduce((summa, p) => summa + p.bredd, 0);
  const nagonOppen = paneler.length > 0;

  useEffect(() => {
    const body = document.body;
    body.style.setProperty('--dockade-paneler-bredd', `${totalBredd}rem`);
    body.classList.toggle('dockade-paneler', totalBredd > 0);
    return () => body.classList.remove('dockade-paneler');
  }, [totalBredd]);

  // Lyssnarna sätts bara upp när en panel faktiskt är öppen — annars hade varje
  // scroll i hela portalen kostat en mätning i onödan.
  useEffect(() => {
    if (!nagonOppen) {
      setToppOffset(0);
      return;
    }

    const mat = () => setToppOffset(navbarensUnderkant());
    mat();

    window.addEventListener('scroll', mat, { passive: true });
    window.addEventListener('resize', mat);
    return () => {
      window.removeEventListener('scroll', mat);
      window.removeEventListener('resize', mat);
    };
  }, [nagonOppen]);

  const varde = useMemo(
    () => ({ paneler, registrera, avregistrera, toppOffset }),
    [paneler, registrera, avregistrera, toppOffset]
  );

  return (
    <DockadePanelerContext.Provider value={varde}>
      {children}
    </DockadePanelerContext.Provider>
  );
};

/**
 * Anmäler en panel så länge `aktiv` är sant och returnerar var den ska ligga:
 * `hoger` i rem från högerkanten, `topp` i px från överkanten.
 *
 * Saknas providern får panelen nollor och beter sig som förr — den kraschar
 * inte, den staplas bara inte och lägger sig över navbaren.
 */
export const useDockadPanel = (id, aktiv, breddRem) => {
  const ctx = useContext(DockadePanelerContext);
  const registrera = ctx?.registrera;
  const avregistrera = ctx?.avregistrera;

  useEffect(() => {
    if (!aktiv || !registrera || !avregistrera) return;
    registrera(id, breddRem);
    return () => avregistrera(id);
  }, [aktiv, id, breddRem, registrera, avregistrera]);

  if (!ctx) return { hoger: 0, topp: 0 };

  const index = ctx.paneler.findIndex(p => p.id === id);
  const hoger = index <= 0 ? 0 : ctx.paneler.slice(0, index).reduce((summa, p) => summa + p.bredd, 0);

  return { hoger, topp: ctx.toppOffset };
};

export default DockadePanelerContext;
